import React, { FC, useEffect } from "react";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import styles from "./TenantUpdate.module.scss";
import schema from "./TenantUpdate.schema";
import Modal from "Components/UI/Modal";
import Form from "Components/UI/Form/Form";
import Field from "Components/UI/Field";
import Button from "Components/UI/Button";
import SelectForm from "Components/UI/SelectForm";
import { AdmActions } from "Store/Adm/Adm.actions";
import { IGlobalReducerState } from "Store/Base/interface/IGlobalReducerState";
import { IOptionData } from "Components/UI/SelectForm";
import { Divider, InputNumber, Skeleton } from "antd";
import { IUserStoreItemRequest } from "Data/interfaces/Store/IUserStoreItemRequest";
import { PaymentMethodEnum } from "Data/interfaces/Store/IUserStoreItem";
import { getCurrentTenant, getTenants } from "Store/Tenant/Tenant.selector";
import { useQueryTenantDetails } from "Hooks/adm/useQueryTenantDetails";
import Tabs from "Components/UI/Tabs/Tabs";

interface ITenantUpdate {
  tenantId: number;
  tenantName: string;
  onClose: () => void;
  closeEditTenantUpdateModal: () => void;
  visible?: boolean;
}

const TenantUpdate: FC<Props> = ({
  tenantId,
  tenantName,
  Planos,
  onClose,
  closeEditTenantUpdateModal,
  visible,
  tenantIsLoading,
  userInfo,
}) => {
  const dispatch = useDispatch();
  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);

  const [isSubmit, setIsSubmit] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const [defaultValues, setDefaultValues] = React.useState({} as IUserStoreItemRequest);
  const [tenant, setTenant] = React.useState<IUserStoreItemRequest | null>(null);
  const [simpleCreditsToAdd, setSimpleCreditsToAdd] = React.useState(0);
  const [complexCreditsToAdd, setComplexCreditsToAdd] = React.useState(0);

  const simpleError = simpleCreditsToAdd > 0 && simpleCreditsToAdd < (tenant?.qtySimpleFilesRendered ?? 0);
  const complexError = complexCreditsToAdd > 0 && complexCreditsToAdd < (tenant?.qtyComplexFilesRendered ?? 0);

  const { data: tenantDetails, isLoading, } = useQueryTenantDetails(tenantId);

  useEffect(() => {
    dispatch(AdmActions.listPlans());
  }, [])

  useEffect(() => {
    if (tenantDetails.Tenant) {
      const tenantAux = {
        UserStoreItemsId: tenantDetails.UserStoreItem.UserStoreItemsId,
        billingDay: tenantDetails.UserStoreItem.BillingDay,
        maxQtyConstructionSites: tenantDetails.UserStoreItem.MaxQtyConstructionSites,
        maxQtyUploadFiles: tenantDetails.UserStoreItem.MaxQtyUploadFiles,
        maxQtySimpleRenderJobs: tenantDetails.UserStoreItem.MaxQtySimpleRenderJobs,
        maxQtyComplexRenderJobs: tenantDetails.UserStoreItem.MaxQtyComplexRenderJobs,
        qtySimpleFilesRendered: tenantDetails.QtySimpleFilesRendered,
        qtyComplexFilesRendered: tenantDetails.QtyComplexFilesRendered,
        price: tenantDetails.UserStoreItem.Price,
        storePlanFk: tenantDetails.UserStoreItem.StorePlan?.StorePlansId,
        contractStartDate: tenantDetails.UserStoreItem.ContractStartDate,
        contractEndDate: tenantDetails.UserStoreItem.ContractEndDate,
        contractPrice: tenantDetails.UserStoreItem.ContractPrice,
        isActive: tenantDetails.UserStoreItem.isActive,
        paymentMethod: tenantDetails.UserStoreItem.PaymentMethod,
        tenantIdTracking: tenantDetails.Tenant.TenantId,
        oldStorePlanTracking: tenantDetails.UserStoreItem.StorePlan?.Name,
        oldStorePlanOrderTracking: tenantDetails.UserStoreItem.StorePlan?.Order,
      };
      setDefaultValues(tenantAux);
      setTenant(tenantAux);
      setSimpleCreditsToAdd(tenantDetails.UserStoreItem.MaxQtySimpleRenderJobs ?? 0);
      setComplexCreditsToAdd(tenantDetails.UserStoreItem.MaxQtyComplexRenderJobs ?? 0);
    }
  }, [tenantDetails])

  useEffect(() => {
    if (isSending && !tenantIsLoading) {
      setIsSending(false);
      closeEditTenantUpdateModal();
      onCloseAux();
    }
  }, [tenantIsLoading])

  const submit = (values: IUserStoreItemRequest) => {
    if (values && !simpleError && !complexError) {
      setIsSending(true);

      const newPlanOrder = Planos?.find(plan => plan.StorePlansId === values.storePlanFk)?.Order;
      const isUpgrade = (newPlanOrder || 0) > (tenant?.oldStorePlanOrderTracking || 0);
      const isUpdate = (newPlanOrder || 0) !== (tenant?.oldStorePlanOrderTracking || 0);
      const request: IUserStoreItemRequest = {
        ...values,
        maxQtySimpleRenderJobs: simpleCreditsToAdd,
        maxQtyComplexRenderJobs: complexCreditsToAdd,
        isUpdateTracking: isUpdate,
        isUpgradeTracking: isUpgrade,
        tenantIdTracking: tenant?.tenantIdTracking,
        oldStorePlanTracking: tenant?.oldStorePlanTracking,
        userInfoTracking: userInfo,
        currentTenantTracking: currentTenant,
        tenantIds: tenants && tenants.length > 0
          ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
          : undefined,
      }

      dispatch(AdmActions.updateTenant(request));
    }
    setIsSubmit(false);
  }

  const onCloseAux = () => {
    onClose();
  }

  const buttons = (
    <div>
      <Button
        onClick={onCloseAux}
      >
        <span>Cancelar</span>
      </Button>
      <Button
        type="primary"
        htmlType="submit"
        loading={tenantIsLoading}
        onClick={() => setIsSubmit(true)}
      >
        <span>Atualizar</span>
      </Button>
    </div>
  );

  const plansRoles = (Planos?.map((plan) => (
    { value: plan.StorePlansId, description: (plan.isActive ? plan.Name : `${plan.Name} (Inativo)`) }
  ))
  ) as IOptionData[];

  const methodsOptions = [
    { value: 1, description: PaymentMethodEnum[1] },
    { value: 2, description: PaymentMethodEnum[2] },
  ] as IOptionData[];

  const statusOptions = [
    { value: 1, description: 'Ativo' },
    { value: 0, description: 'Inativo' },
  ] as IOptionData[];

  return (
    <Modal
      title="Gerenciamento de Tenant"
      visible={visible}
      footerButtons={buttons}
      onCancel={onCloseAux}
      width={676}
    >
      <div
        className={styles['form-wrapper']}
        key={`status-${tenantName}`}
      >
        {isLoading || tenantIsLoading ? (
          <Skeleton active />
        ) : (
          <>
            <div>
              <h4>{tenantName}</h4>
            </div>

            <Form
              schema={schema}
              onSubmit={submit}
              isSubmited={isSubmit}
              resetForm={true}
              initialValues={defaultValues}
            >
              <Tabs
                tabs={[
                  {
                    title: 'Plano & Contrato',
                    tabKey: 'plan',
                    children: (
                      <div className={styles['field-main-wrapper']}>
                        <Divider orientation="left" style={{ color: '#BDBDBD' }}>Dados do Plano</Divider>

                        <div className={styles['field-wrapper']}>
                          <Field
                            label="Max Obras"
                            name="maxQtyConstructionSites"
                            type="number"
                            className={styles['field-input']}
                          />
                          <Field
                            label="Max Uploads"
                            name="maxQtyUploadFiles"
                            type="number"
                            className={styles['field-input']}
                          />
                        </div>

                        <div className={styles['field-wrapper']}>
                          <div className={styles['field-input']}>
                            <SelectForm
                              placeholder="Selecione o plano"
                              options={plansRoles}
                              name='storePlanFk'
                            />
                            <span className={styles['field-label']}>Plano</span>
                          </div>
                          <Field
                            label="Preço"
                            name="price"
                            type="number"
                            className={styles['field-input']}
                          />
                        </div>

                        <Divider orientation="left" style={{ color: '#BDBDBD' }}>Dados do Contrato</Divider>

                        <div className={styles['field-wrapper']}>
                          <div className={styles['field-input']}>
                            <SelectForm
                              placeholder="Selecione o status"
                              options={statusOptions}
                              name='isActive'
                            />
                            <span className={styles['field-label']}>Status</span>
                          </div>
                          <Field
                            label="Início do Contrato"
                            name="contractStartDate"
                            type="date"
                            className={styles['field-input']}
                          />
                          <Field
                            label="Fim do Contrato"
                            name="contractEndDate"
                            type="date"
                            className={styles['field-input']}
                          />
                        </div>
                        <div className={styles['field-wrapper']}>
                          <div className={styles['field-input']}>
                            <SelectForm
                              placeholder="Selecione o método"
                              options={methodsOptions}
                              name='paymentMethod'
                            />
                            <span className={styles['field-label']}>Método Pagamento</span>
                          </div>
                          <Field
                            label="Valor do Contrato"
                            name="contractPrice"
                            type="number"
                            className={styles['field-input']}
                          />
                          <Field
                            label="Dia de Faturamento"
                            name="billingDay"
                            type="number"
                            className={styles['field-input']}
                          />
                        </div>
                      </div>
                    )
                  },
                  {
                    title: 'Renderização',
                    tabKey: 'rendering',
                    children: (
                      <div className={styles['field-main-wrapper']}>
                        <Divider orientation="left" style={{ color: '#BDBDBD' }}>Renderização Simples</Divider>
                        <div className={styles['render-section']}>
                          <span className={styles['render-current-label']}>
                            Qtde renderizações simples: <strong>{tenant?.qtySimpleFilesRendered ?? 0}</strong>
                          </span>
                          <div className={styles['render-credit-row']}>
                            <InputNumber
                              min={0}
                              value={simpleCreditsToAdd}
                              onChange={(val) => setSimpleCreditsToAdd(val ?? 0)}
                              className={styles['render-input']}
                              status={simpleError ? 'error' : undefined}
                            />
                            <Button onClick={() => setSimpleCreditsToAdd((v) => v + 5)}>+5</Button>
                            <Button onClick={() => setSimpleCreditsToAdd((v) => v + 10)}>+10</Button>
                            <Button onClick={() => setSimpleCreditsToAdd((v) => v + 50)}>+50</Button>
                          </div>
                          {simpleError && (
                            <span className={styles['render-error']}>
                              O valor deve ser igual ou maior que {tenant?.qtySimpleFilesRendered}
                            </span>
                          )}
                        </div>

                        <Divider orientation="left" style={{ color: '#BDBDBD' }}>Renderização Complexa</Divider>
                        <div className={styles['render-section']}>
                          <span className={styles['render-current-label']}>
                            Qtde renderizações complexas: <strong>{tenant?.qtyComplexFilesRendered ?? 0}</strong>
                          </span>
                          <div className={styles['render-credit-row']}>
                            <InputNumber
                              min={0}
                              value={complexCreditsToAdd}
                              onChange={(val) => setComplexCreditsToAdd(val ?? 0)}
                              className={styles['render-input']}
                              status={complexError ? 'error' : undefined}
                            />
                            <Button onClick={() => setComplexCreditsToAdd((v) => v + 5)}>+5</Button>
                            <Button onClick={() => setComplexCreditsToAdd((v) => v + 10)}>+10</Button>
                            <Button onClick={() => setComplexCreditsToAdd((v) => v + 50)}>+50</Button>
                          </div>
                          {complexError && (
                            <span className={styles['render-error']}>
                              O valor deve ser igual ou maior que {tenant?.qtyComplexFilesRendered}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  }
                ]}
              />
            </Form>
          </>
        )}
      </div>
    </Modal>
  )
}

const mapState = (state: IGlobalReducerState) => ({
  ...state.adm,
  userInfo: state.auth.userInfo,
})

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>
type Props = PropsFromRedux & ITenantUpdate;

export default connector(TenantUpdate);